import * as React from 'react'
import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@lightspeed/design-system-react'
import classNames from 'classnames'
import { useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { STEPS } from '../types'
import {
  CONTACT_OPTIONS,
  CustomerInfo,
  CUSTOMER_NAME_FIELD,
} from '../../../common/types'
import {
  getDefaultContactOption,
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { ContactNumber } from '../../../common/ContactNumber'

interface Props {
  onClose: () => void
  onStepChange: (step: STEPS) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  newCustomer: CustomerInfo | null
}

const shouldDisableNext = (customer: CustomerInfo | null) => {
  const name: string[] = []
  let hasAllNeededCustomerInfo = false
  if (customer) {
    Object.values(CUSTOMER_NAME_FIELD).forEach(option => {
      const value = customer[option]
      if (value && value.trim()) {
        name.push(option)
      }
    })
    if (hasContactInfo(customer) && name.length > 0) {
      hasAllNeededCustomerInfo = true
    }
  }

  return !hasAllNeededCustomerInfo || isInvalidEmail(customer)
}

shouldDisableNext(null)

export const Step1Add: React.FC<Props> = ({
  onStepChange,
  onClose,
  onChange,
  newCustomer,
}) => {
  const t = useT()
  const formLabelContext = 'Form label in quote customer modal'
  const inputPlaceholderContext = 'Input placeholder in quote customer modal'
  const buttonTextContext = 'Button text in quote customer modal'

  const copies = {
    header: t('Add contact information', {
      _context: 'Header in quote customer modal add step',
    }),
    description: t('Add customer details to contact them about this quote.', {
      _context: 'Description in quote customer modal add step',
    }),
    firstName: t('First name', {
      _context: formLabelContext,
    }),
    enterFirstName: t('Enter first name', {
      _context: inputPlaceholderContext,
    }),
    lastName: t('Last name', {
      _context: formLabelContext,
    }),
    enterLastName: t('Enter last name', {
      _context: inputPlaceholderContext,
    }),
    contactNumber: t('Contact number', {
      _context: formLabelContext,
    }),
    email: t('Email', {
      _context: formLabelContext,
    }),
    enterEmail: t('Enter email', {
      _context: inputPlaceholderContext,
    }),
    back: t('Back', {
      _context: buttonTextContext,
    }),
    next: t('Next', {
      _context: buttonTextContext,
    }),
  }

  return (
    <Modal onClose={onClose}>
      <StepBreadcrumbs step={STEPS.ADD_NEW_CUSTOMER} isAddingCustomer={true} />
      <ModalHeader>
        {copies.header}
        <Text tag="p" className="vd-mt2">
          {copies.description}
        </Text>
      </ModalHeader>
      <ModalContent>
        <>
          <div className="vd-g-row">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                {copies.firstName}
              </div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder={copies.enterFirstName}
                  value={newCustomer!.firstName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.FIRSTNAME}
                />
              </div>
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                {copies.lastName}
              </div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder={copies.enterLastName}
                  value={newCustomer!.lastName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.LASTNAME}
                />
              </div>
            </div>
          </div>
          <div className="vd-g-row vd-mt4">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                {copies.contactNumber}
              </div>
              <ContactNumber
                customer={newCustomer}
                onChange={onChange}
                defaultOption={getDefaultContactOption(newCustomer)}
              />
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">{copies.email}</div>
              <div className="vd-value">
                <input
                  className={classNames('vd-input', {
                    'vd-input--error': isInvalidEmail(newCustomer),
                  })}
                  placeholder={copies.enterEmail}
                  value={newCustomer!.email || ''}
                  onChange={onChange}
                  name={CONTACT_OPTIONS.EMAIL}
                />
              </div>
            </div>
          </div>
        </>
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={() => onStepChange(STEPS.SELECT_CUSTOMER)}
            data-cy="button-back"
            data-track="back-select-customer-step"
          >
            {copies.back}
          </Button>
          <Button
            variant="go"
            onClick={() => onStepChange(STEPS.REVIEW_DETAILS)}
            disabled={shouldDisableNext(newCustomer)}
            data-cy="button-next"
            data-track="next-review-details-step"
          >
            {copies.next}
          </Button>
        </div>
      </ModalActions>
    </Modal>
  )
}

Step1Add.displayName = 'Step1Add'
